import { Component, Index, JSX, Show } from 'solid-js';
import { Icon } from '@tcweb/connect-ui';
import { ControlMenuActionRow } from './ControlMenuActionRow';
import { ControlMenuCheckboxRow } from './ControlMenuCheckboxRow';
import { ControlMenuRadioGroupOptions } from './ControlMenuRadioGroupOptions';
import { ControlMenuSubmenu } from './ControlMenuSubmenu';
import { ControlMenuToggleRow } from './ControlMenuToggleRow';
import {
  isCheckboxIndeterminate,
  setCheckboxValueRecursively,
} from './controlMenuCheckboxUtils';
import {
  ActionMenuItem,
  CheckboxMenuItem,
  ControlMenuItemType,
  ControlMenuNestedSubmenuProps,
  ControlMenuNestedSubmenuSharedProps,
  RadioGroupMenuItem,
  RadioOption,
  ToggleMenuItem,
} from './types';

interface ControlMenuItemProps {
  item: ControlMenuItemType;
  isMobile: boolean;
  openMenus: Set<string>;
  setOpenMenus(updater: (prev: Set<string>) => Set<string>): void;
  onNavigate(item: ControlMenuItemType): void;
  onClose?(): void;
  parentOpensUpward?: boolean;
}

function ControlMenuNestedSubmenu(
  props: ControlMenuNestedSubmenuProps
): JSX.Element {
  return (
    <ControlMenuSubmenu parentOpensUpward={props.parentOpensUpward}>
      {/* Read children inside Index's own memo so a parent item rebuild
          reconciles rows in place instead of recreating the whole submenu. */}
      <Index each={props.getChildrenItems()}>
        {(child) => (
          <ControlMenuItem
            item={child()}
            isMobile={props.isMobile}
            openMenus={props.openMenus}
            setOpenMenus={props.setOpenMenus}
            onNavigate={props.onNavigate}
            onClose={props.onClose}
            parentOpensUpward={props.parentOpensUpward}
          />
        )}
      </Index>
    </ControlMenuSubmenu>
  );
}

export const ControlMenuItem: Component<ControlMenuItemProps> = (props) => {
  const isDisabled = (): boolean => {
    const item = props.item;
    if (item.type === 'divider') {
      return false;
    }
    return item.disabled ?? false;
  };

  const getChildren = (): ControlMenuItemType[] => {
    return 'children' in props.item ? (props.item.children ?? []) : [];
  };

  const isIndeterminate = (): boolean => {
    if (props.item.type !== 'checkbox') {
      return false;
    }
    return isCheckboxIndeterminate(
      props.item as CheckboxMenuItem,
      getChildren()
    );
  };

  const handleItemContainerMouseEnter = (): void => {
    if (!props.isMobile && getChildren().length > 0) {
      const itemId = props.item.id;
      props.setOpenMenus((prev) => {
        const next = new Set(prev);
        next.add(itemId);
        return next;
      });
    }
  };

  const handleItemContainerMouseLeave = (): void => {
    if (!props.isMobile) {
      const itemId = props.item.id;
      props.setOpenMenus((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleChevronClick = (e: MouseEvent): void => {
    e.stopPropagation();
    if (props.isMobile && getChildren().length > 0 && !isDisabled()) {
      props.onNavigate(props.item);
    }
  };

  const handleCheckboxClick = (e: Event): void => {
    e.stopPropagation();
    if (!isDisabled() && props.item.type === 'checkbox') {
      const checkboxItem = props.item as CheckboxMenuItem;
      const newValue = !checkboxItem.value();
      setCheckboxValueRecursively(checkboxItem, newValue);
    }
  };

  const handleToggleClick = (e: Event): void => {
    e.stopPropagation();
    if (!isDisabled() && props.item.type === 'toggle') {
      const toggleItem = props.item as ToggleMenuItem;
      toggleItem.setValue(!toggleItem.value());
    }
  };

  const handleActionClick = (e: Event): void => {
    e.stopPropagation();
    if (!isDisabled() && props.item.type === 'action') {
      const actionItem = props.item as ActionMenuItem;
      actionItem.onClick();
      props.onClose?.();
    }
  };

  const handleRadioOptionClick = (
    e: Event,
    option: RadioOption,
    hasChildren: boolean
  ): void => {
    e.stopPropagation();
    if (!isDisabled() && props.item.type === 'radiogroup') {
      // On mobile, if option has children, navigate to submenu instead of selecting
      if (props.isMobile && hasChildren && option.children) {
        const submenuItem: CheckboxMenuItem = {
          id: option.id,
          type: 'checkbox',
          label: option.label,
          value: () => false,
          setValue: () => {
            // No-op: this is just a navigation wrapper
          },
          children: option.children,
        };
        props.onNavigate(submenuItem);
        return;
      }

      if (!props.isMobile && hasChildren) {
        return;
      }

      const radioItem = props.item as RadioGroupMenuItem;
      if (radioItem.value() === option.id) {
        if (radioItem.emitWhenAlreadySelected) {
          radioItem.setValue(option.id);
          props.onClose?.();
          return;
        }
        if (radioItem.isUnselectionAllowed) {
          radioItem.setValue('');
        }
        props.onClose?.();
        return;
      }
      radioItem.setValue(option.id);
      props.onClose?.();
    }
  };

  const getSharedSubmenuProps = (): ControlMenuNestedSubmenuSharedProps => ({
    isMobile: props.isMobile,
    openMenus: props.openMenus,
    setOpenMenus: props.setOpenMenus,
    onNavigate: props.onNavigate,
    onClose: props.onClose,
    parentOpensUpward: props.parentOpensUpward,
  });

  const handleRowClick = (e: Event): void => {
    if (props.item.type === 'checkbox') {
      handleCheckboxClick(e);
      return;
    }
    if (props.item.type === 'toggle') {
      handleToggleClick(e);
      return;
    }
    if (props.item.type === 'action') {
      handleActionClick(e);
    }
  };

  const getItemClasses = (): string => {
    const baseClasses =
      'relative flex items-center justify-between min-w-0 flex-1';
    if (props.item.type === 'radiogroup') {
      return baseClasses;
    }
    const paddingClass = 'p-2';
    const isSelectedAction =
      props.item.type === 'action' && props.item.selected;
    const interactiveClasses = isDisabled()
      ? 'opacity-50 cursor-not-allowed'
      : isSelectedAction
        ? 'cursor-pointer bg-primary-pale'
        : 'cursor-pointer hover:bg-[var(--modus-wc-color-base-100)]';
    return `${baseClasses} ${paddingClass} ${interactiveClasses}`;
  };

  const itemContent = (
    <div class={getItemClasses()} onClick={(event) => handleRowClick(event)}>
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <Show when={props.item.type === 'checkbox'}>
          <ControlMenuCheckboxRow
            item={props.item as CheckboxMenuItem}
            isIndeterminate={isIndeterminate()}
          />
        </Show>

        <Show when={props.item.type === 'toggle'}>
          <ControlMenuToggleRow item={props.item as ToggleMenuItem} />
        </Show>

        <Show when={props.item.type === 'action'}>
          <ControlMenuActionRow item={props.item as ActionMenuItem} />
        </Show>

        <Show when={props.item.type === 'radiogroup'}>
          <ControlMenuRadioGroupOptions
            item={props.item as RadioGroupMenuItem}
            isMobile={props.isMobile}
            openMenus={props.openMenus}
            setOpenMenus={props.setOpenMenus}
            disabled={isDisabled()}
            onNavigate={props.onNavigate}
            onClose={props.onClose}
            parentOpensUpward={props.parentOpensUpward}
            onRadioOptionClick={handleRadioOptionClick}
            submenuComponent={ControlMenuNestedSubmenu}
            submenuProps={getSharedSubmenuProps()}
          />
        </Show>
      </div>

      <Show when={getChildren().length > 0}>
        <div
          class="cursor-pointer"
          data-testid="control-menu-item-chevron"
          onClick={handleChevronClick}
        >
          <Icon icon="tc-icon-chevron-right" class="text-base-content" />
        </div>
      </Show>
    </div>
  );

  return (
    <Show
      when={props.item.type !== 'divider'}
      fallback={<hr class="my-1 border-[var(--modus-wc-color-base-200)]" />}
    >
      <Show
        when={!props.isMobile}
        fallback={<div class="relative">{itemContent}</div>}
      >
        <div
          class="relative"
          data-testid="control-menu-item-container"
          onMouseEnter={handleItemContainerMouseEnter}
          onMouseLeave={handleItemContainerMouseLeave}
        >
          {itemContent}
          <Show
            when={
              !props.isMobile &&
              props.openMenus.has(props.item.id) &&
              getChildren().length > 0
            }
          >
            <ControlMenuNestedSubmenu
              getChildrenItems={getChildren}
              {...getSharedSubmenuProps()}
            />
          </Show>
        </div>
      </Show>
    </Show>
  );
};
